import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import useIsMobile from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { isCustomizeDressAttribute } from '../../isCustomizeDressAttribute';
import { forMatLabel } from '../../lib/format-label';
import { SwatchDataValue } from '../../types/data';
import type { Item, ProductRoot, Value } from '../../types/product';

type CustomizeDressOptionsProps = {
  item: Item;
  product: ProductRoot;
  customAttributeTreeData: any;
};

export const CustomizeDressOptions = ({ item, product, customAttributeTreeData }: CustomizeDressOptionsProps) => {
  const form = useFormContext();

  const { watch, setValue } = form;

  const allFormData = watch();

  const isMobile = useIsMobile();

  const fabric = form.watch('Fabric');

  const selectedValue: Value | null = allFormData ? allFormData[forMatLabel(item.label)] : null;

  const availableOptions = useMemo(() => {
    if (!customAttributeTreeData) return null;

    const selectedCustomizeDressLabels: Array<string> = [];

    selectedCustomizeDressLabels.push(fabric.label);

    product.ampProtOption.items.every(temp => {
      if (temp.label === item.label) {
        return false;
      } else {
        if (isCustomizeDressAttribute(temp.label)) {
          const selected = allFormData ? allFormData[forMatLabel(temp.label)] : null;

          if (selected) {
            selectedCustomizeDressLabels.push(selected.label.split(' ').join('-'));
          }
        }
        return true;
      }
    });

    let result = null;

    selectedCustomizeDressLabels.forEach(label => {
      if (result && result[label]) {
        result = result[label];
      } else {
        result = customAttributeTreeData[label];
      }
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFormData, fabric]);

  useEffect(() => {
    if (selectedValue && availableOptions && !availableOptions[selectedValue.label.split(' ').join('-')]) {
      const availableOptionsArr = Object.keys(availableOptions);

      const availableFirstValue = item.values.find(
        temp => availableOptionsArr.indexOf(temp.label.split(' ').join('-')) > -1,
      );

      form.setValue(forMatLabel(item.label), {
        value_index: availableFirstValue?.value_index.toString(),
        label: availableFirstValue?.label,
        price: availableFirstValue?.price,
        option_id: availableFirstValue?.option_id?.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue, availableOptions, form, allFormData]);

  const optionsLength = item?.values.filter(
    temp => availableOptions && Object.keys(availableOptions).indexOf(temp.label.split(' ').join('-')) > -1,
  ).length;

  return (
    <>
      <FormField
        key={item?.uid}
        control={form.control}
        name={forMatLabel(item.label)}
        render={({ field }) => {
          return (
            <div>
              <FormItem className={clsx('bg-white  px-2.5 py-[8px] lg_p-0')}>
                {!isMobile && (
                  <FormLabel
                    className='mb-2.5 flex flex-wrap items-center justify-between gap-1 '
                    id={item.label}
                  >
                    <div className='flex items-center gap-x-2.5'>
                      <span className='text-base font-medium uppercase leading-5 lg_font-normal'>{item.label}: </span>
                      <span className=' text-[15px] leading-5 text-#403732 lg_text-80583F'>{selectedValue?.label}</span>
                    </div>
                  </FormLabel>
                )}
                <FormMessage />
                <FormControl>
                  <RadioGroup
                    suppressHydrationWarning
                    onValueChange={value => {
                      const selectedOption = item?.values.find(v => v.value_index.toString() === value);

                      if (selectedOption) {
                        field.onChange({
                          value_index: value,
                          label: selectedOption?.label,
                          price: selectedOption?.price,
                          option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                        });
                      }
                    }}
                    value={field.value?.value_index}
                    className={cn(
                      'flex flex-wrap gap-x-[18px] gap-y-0.5 md_justify-start',
                      optionsLength > 2 ? 'justify-between' : 'justify-center',
                    )}
                  >
                    {item?.values.map(option => {
                      if (
                        availableOptions &&
                        Object.keys(availableOptions).indexOf(option.label.split(' ').join('-')) > -1
                      ) {
                        return (
                          <CustomizeDressRadioItem
                            key={option.uid}
                            item={option}
                            field={field}
                            selectedValue={selectedValue}
                            isOutOfStock={false}
                          />
                        );
                      }
                    })}
                  </RadioGroup>
                </FormControl>
              </FormItem>
            </div>
          );
        }}
      ></FormField>
    </>
  );
};

const CustomizeDressRadioItem = ({
  item,
  selectedValue,
  isOutOfStock,
}: {
  item: SwatchDataValue;
  selectedValue: any;
  isOutOfStock: boolean;
}) => {
  const isMobile = useIsMobile();

  const [hovered, setHoveered] = useState(false);

  const active = selectedValue.value_index.toString() === item.value_index.toString();

  const icon = `/customize-dress-options/${item.label.split(' ').join('-')}.svg`;

  const activeIcon = `/customize-dress-options/${item.label.split(' ').join('-')}-active.svg`;

  return (
    <FormItem className={cn('flex items-start space-y-0', isOutOfStock && 'hidden')}>
      <FormControl>
        <RadioGroupItem
          value={item.value_index.toString()}
          className='peer sr-only'
          suppressHydrationWarning
        />
      </FormControl>
      <FormLabel
        className={cn('md__min-w-[80px] group flex cursor-pointer flex-col items-center justify-center')}
        onMouseEnter={() => {
          setHoveered(true);
        }}
        onMouseLeave={() => {
          setHoveered(false);
        }}
      >
        <div className={cn('h-[70px] w-[56px] border border-solid border-white md_h-[76px]')}>
          <img
            src={hovered || active ? activeIcon : icon}
            className='h-auto w-full'
          />
        </div>
        {!isMobile && (
          <div className='w-[80px] text-center text-[13px] font-[400] leading-[1.4] text-[#403732]'>{item.label}</div>
        )}
      </FormLabel>
    </FormItem>
  );
};
